import React, { useState } from 'react';
import { ExternalLink, ShoppingCart, Star, Filter, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { tptProducts, amazonProducts } from '../mock';

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'Vocabulary', 'ESL/ELL', 'SPED', 'Homeschool', 'Activities'];

  const filteredProducts = selectedCategory === 'all'
    ? tptProducts
    : tptProducts.filter(product => product.category === selectedCategory);

  return (
    <div className="min-h-screen py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-foreground">
            Our <span className="gradient-text">Educational Products</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Quality resources designed for autism, SPED, ESL, and diverse learners. Available on Amazon and Teachers Pay Teachers.
          </p>
        </div>

        {/* Amazon Featured Product */}
        <section className="mb-20">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 sm:p-12 border-2 border-amber-200">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-accent" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">Featured on Amazon</h2>
            </div>
            {amazonProducts.map((product) => (
              <div key={product.id} className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-96 object-cover rounded-xl shadow-lg"
                  />
                </div>
                <div>
                  <h3 className="text-3xl font-bold mb-4 text-foreground">{product.title}</h3>
                  <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                    {product.description}
                  </p>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-5 w-5 ${
                            i < Math.floor(product.rating)
                              ? 'fill-accent text-accent'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-muted-foreground">
                      {product.rating} ({product.reviews} reviews)
                    </span>
                  </div>
                  <Button
                    asChild
                    size="lg"
                    className="bg-accent hover:bg-accent/90 text-lg px-8"
                  >
                    <a href={product.amazonLink} target="_blank" rel="noopener noreferrer">
                      View on Amazon
                      <ExternalLink className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* TPT Products */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">
                Teachers Pay Teachers <span className="gradient-text">Collection</span>
              </h2>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center space-x-3 mb-10 overflow-x-auto pb-4">
            <Filter className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <span className="text-sm font-medium text-muted-foreground flex-shrink-0">Filter:</span>
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? 'bg-primary' : ''}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <Card key={product.id} className="overflow-hidden border-2 hover:border-primary/50 transition-all card-hover group">
                <div className="relative overflow-hidden h-56">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <Badge className="absolute top-4 right-4 bg-primary text-white">
                    {product.category}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <div className="mb-3">
                    <Badge variant="outline" className="text-xs">
                      Grades {product.grades}
                    </Badge>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground line-clamp-2">
                    {product.title}
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed line-clamp-3">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary">{product.price}</span>
                    <Button variant="outline" size="sm" className="group-hover:bg-primary group-hover:text-white transition-colors">
                      Learn More
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Visit Store CTA */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-10 border-2 border-border">
              <h3 className="text-2xl font-bold mb-4 text-foreground">Explore Our Complete Collection</h3>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Visit our Teachers Pay Teachers store to browse our full range of educational resources for diverse learners.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90"
              >
                <a href="https://www.teacherspayteachers.com/store/embracethemagicofwords" target="_blank" rel="noopener noreferrer">
                  Visit TPT Store
                  <ExternalLink className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Products;